import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    error: Error | null;
}

const CHUNK_RELOAD_KEY = "crm:chunk-load-reload";

function isStaleChunkLoadError(error: Error): boolean {
    const text = `${error.name} ${error.message}`.toLowerCase();
    return (
        text.includes("dynamically imported module")
        || text.includes("loading css chunk")
        || text.includes("loading chunk")
        || text.includes("failed to fetch dynamically imported module")
        || text.includes("importing a module script failed")
    );
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = {
            error: null,
        };
    }

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Unhandled application error", error, errorInfo);

        // След deploy старите lazy chunk-ове липсват — едно автоматично презареждане взима новия index.
        if (!isStaleChunkLoadError(error)) {
            return;
        }

        try {
            if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1") {
                return;
            }
            sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
            window.location.reload();
        } catch {
            // sessionStorage недостъпен — оставяме екрана с бутон Презареди
        }
    }

    public componentDidMount() {
        // Не чистим флага веднага — иначе при още стар кеширан index зацикляме reload.
        window.setTimeout(() => {
            if (this.state.error) {
                return;
            }
            try {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
            } catch {
                // ignore
            }
        }, 3000);
    }

    public render() {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        const staleChunk = isStaleChunkLoadError(error);

        return (
            <Box
                sx={{
                    minHeight: "100dvh",
                    display: "grid",
                    placeItems: "center",
                    padding: 3,
                }}
            >
                <Paper sx={{ maxWidth: 560, width: "100%", padding: 4 }}>
                    <Stack spacing={2.5}>
                        <Typography variant="overline" color="primary.main">
                            {staleChunk ? "Обновена версия" : "Системна грешка"}
                        </Typography>
                        <Typography variant="h4">
                            {staleChunk
                                ? "Има нова версия на приложението."
                                : "Нещо в приложението се счупи неочаквано."}
                        </Typography>
                        <Alert severity={staleChunk ? "info" : "error"}>
                            {staleChunk
                                ? "Презареди страницата, за да се заредят новите файлове."
                                : "Екранът беше спрян, за да не остане приложението в невалидно състояние."}
                        </Alert>
                        <Typography color="text.secondary">
                            {staleChunk
                                ? "Това се случва понякога веднага след качване на нова версия."
                                : "Можеш да презаредиш страницата. Ако проблемът се повтори, се обади на програмистите."}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => window.location.reload()}
                        >
                            Презареди
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        );
    }
}
